import { useQueryParams, useSubmit } from "@certn/api";
import { Text, BasicButton } from "@certn/cascadia";
import {
  BrandAsset,
  Card,
  Layout,
  LinkButton,
  showToast,
  TextInputFormikField,
  useColors,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { object, string } from "yup";

export function CRCVerifEnterCodePage() {
  const { params } = useQueryParams();
  const { submit } = useSubmit(
    `/api/internal/applicant/mycrc/verify-code-for-certificate/`,
    {
      onSuccess: (res: any) => {
        window.location.href = res.url;
      },
      onFailure: (error: any) => {
        switch (error.status) {
          case 429:
            showToast(
              t("Too many verification codes requested. Try again later."),
              {
                type: "error",
              }
            );
            break;
          case 400:
            showToast(
              t(
                "The access code entered is not current. Please enter the most recent access code requested."
              ),
              {
                type: "error",
              }
            );
            break;
          case 404:
            showToast(
              t(
                "No valid verification codes exist. Please try requesting a new code."
              ),
              {
                type: "error",
              }
            );
            break;
          case 501:
            showToast(t("Unavailable"), { type: "error" });
            break;
          default:
            console.error(error);
            showToast(t("Something went wrong"), { type: "error" });
        }
      },
    }
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const colors = useColors();
  const codeSchema = object().shape({
    code: string().required().length(6).label("Code"),
  });

  async function handleSubmit(values: { code: string }) {
    await submit({
      code: values.code,
      case_id: params.caseId,
      email_address: params.email,
    });
  }

  return (
    <Layout backgroundColor="surface-sunken">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--space-6)",
        }}
      >
        <div style={{ paddingBottom: "var(--space-6)" }}>
          <BrandAsset brand="mycrc" height={32} width={130} />
        </div>
        <Card className={styles.card} border="dropshadow">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "var(--space-6)",
            }}
          >
            <Text style="global/heading-medium">{t("View my results")}</Text>
            <Text>
              {t(
                "To securely access your details, enter the 6 character code found in our previous email."
              )}
            </Text>
            <Formik
              initialValues={{ code: "" }}
              validationSchema={codeSchema}
              onSubmit={handleSubmit}
            >
              {({ handleSubmit }) => (
                <>
                  <TextInputFormikField
                    testID="crc-verification-code"
                    placeholder="Enter 6 character code"
                    style={{ width: "100%" }}
                    name="code"
                    label=""
                    maxLength={6}
                    aria-label="Enter 6 character code"
                  />
                  <BasicButton fullWidth onPress={handleSubmit}>
                    {t("Access details")}
                  </BasicButton>
                </>
              )}
            </Formik>

            <Text>
              {t("Access code expired?")}{" "}
              <LinkButton
                testID="send-new-code"
                linkStyle="monochrome"
                onPress={() => {
                  navigate(
                    `/my-crc/certificate?case_id=${params.caseId}&email=${encodeURIComponent(Array.isArray(params.email) ? params.email[0] : params.email)}`
                  );
                }}
              >
                {t("Send new code")}
              </LinkButton>
            </Text>
          </div>
        </Card>
        <div style={{ marginTop: "var(--space-6)" }}>
          <Text color="text-weak" testID="footer-powered-by-link">
            <Trans>
              Powered by{" "}
              <BrandAsset
                height="18"
                width="62"
                type="full"
                brand="certn"
                fill={colors["icon-base"]}
                className={css({ verticalAlign: "middle" })}
              />
            </Trans>
          </Text>
        </div>
      </div>
    </Layout>
  );
}

const styles = {
  card: css({
    width: "100%",
    maxWidth: "277px",
    padding: "var(--space-6)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  }),
};
